import React, { FC, useState, useEffect, useMemo } from "react";
import styled from "styled-components";

// interfaces, helpers, & children components
import { CartItemProps } from "./common/types";
import { getQuantityOptions } from "./common/helpers";
import { ItemImage } from "./item-children/ItemImage";
import { ItemTitle } from "./item-children/ItemTitle";
import { ItemPrice } from "./item-children/ItemPrice";
import { Subtotal } from "./item-children/Subtotal";
import { FlagAndNote } from "./item-children/FlagAndNote";
import { ItemCustomizations } from "./item-children/ItemCustomizations";
import { ItemQuantitySelect } from "./item-children/ItemQuantitySelect";
import { ItemSubDetails } from "./item-children/ItemSubDetails";
import { KitContents } from "./item-children/KitContents";
import { formatDropdownQty } from "../dropdown/helpers";
import { Divider } from "../divider/Divider";
import { Button } from "../button/Button";
import { ReactSelectDropdownOption } from "../dropdown/types";
import { TimedWarning } from "../alert/TimedWarning";

const StyledCartItem = styled.div`
  display: flex;
  justify-content: flex-start;
  padding: 20px 0;
`;

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 6px;
`;

const Qty = styled.div`
  font-size: 14px;
`;

const StyledItemQuantitySelect = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  height: 31px;
  position: relative;
  margin: 20px 0;
`;

const RemoveItem = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 15px;

  button {
    font-size: 12px;
    font-weight: 500;
    color: #666;
    text-decoration: underline;
  }
`;

const Error = styled.div`
  position: absolute;
  bottom: -5px;
`;

export const CartItem: FC<CartItemProps> = ({
  allowQtyUpdate = true,
  cart,
  currentUserId,
  customizations,
  fallbackUrl = "",
  blockedItem,
  isGuest,
  itemImgCdn,
  itemImgSrc,
  logCartLineItemQtyChangedEvent,
  maxCartQty,
  product,
  quantity,
  subscriptionIntervalInDays,
  typeableSelect,
  updateCartItem,
  isLink = true,
  small,
  hideDivider,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const { compareAtPrice, price, inventoryQty, subscriptionMetadata } = product;
  const { discount, subscriptionPrice } = subscriptionMetadata || {};
  const subtotalPrice =
    (subscriptionIntervalInDays && subscriptionPrice) || price;
  const minQty = product.minOrderQty || 1;
  // Here we only calculate quantity options if allowQtyUpdate is true and
  // we also memoize the result to prevent re-executing the function with the same args.
  const options = useMemo(
    () => (allowQtyUpdate ? getQuantityOptions(product, maxCartQty) : []),
    [product, maxCartQty, allowQtyUpdate]
  );

  const [selectedQuantity, setSelectedQuantity] = useState(
    formatDropdownQty(minQty)
  );

  useEffect(() => {
    if (quantity > 0) {
      setSelectedQuantity(formatDropdownQty(quantity));
    }
  }, [quantity]);

  const updateQty = async (selection?: ReactSelectDropdownOption | null) => {
    if (!product.id) return;
    if (!selection) return;
    try {
      setLoading(true);
      await updateCartItem({
        productId: product.id,
        quantity: parseInt(selection.value),
        subscriptionIntervalInDays: subscriptionIntervalInDays || null,
      });
      logCartLineItemQtyChangedEvent &&
        logCartLineItemQtyChangedEvent(
          currentUserId,
          cart || {},
          product,
          quantity,
          parseInt(selection.value),
          subscriptionIntervalInDays || null
        );
    } catch (err) {
      setSelectedQuantity(formatDropdownQty(quantity));
      setError("Something went wrong. Please try again");
    } finally {
      setLoading(false);
    }
  };

  const doesOptionExist = (str: string) => {
    return options.find((i) => i.label === str);
  };

  const blurAndSelect = (val: string) => {
    if (val && !isNaN(+val)) {
      updateQty({ label: val, value: val });
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!product.id) return;
    updateCartItem({
      productId: product.id,
      quantity: 0,
      subscriptionIntervalInDays: null,
    });
  };

  return (
    <>
      <StyledCartItem data-testid="cart-item">
        <ItemImage
          productId={product.id}
          productTitle={product.title || ""}
          productSlug={product.itemSlug || ""}
          itemImgSrc={itemImgSrc}
          itemImgCdn={itemImgCdn}
          fallbackUrl={fallbackUrl}
          isLink={isLink}
          small={small}
        />

        <DetailsWrapper>
          <ItemTitle
            productId={product.id}
            productTitle={product.title || ""}
            productSlug={product.itemSlug || ""}
            fallbackUrl={fallbackUrl}
            isLink={isLink}
            small={small}
          />

          <ItemPrice
            compareAtPrice={compareAtPrice}
            price={price}
            subscriptionPrice={subscriptionIntervalInDays && subscriptionPrice}
            small={small}
          />

          {!allowQtyUpdate && <Qty>Qty: {quantity}</Qty>}

          {customizations && (
            <ItemCustomizations customizations={customizations} />
          )}

          <ItemSubDetails
            discount={discount}
            interval={subscriptionIntervalInDays || null}
            subscribed={!!subscriptionIntervalInDays}
          />

          {allowQtyUpdate && (
            <StyledItemQuantitySelect>
              <ItemQuantitySelect
                typeableSelect={typeableSelect}
                formatCreateLabel={(str) => str}
                onBlur={(e) =>
                  blurAndSelect((e.target as HTMLInputElement).value)
                }
                onChange={(selected) =>
                  updateQty(selected as ReactSelectDropdownOption)
                }
                noOptionsMessage={() => "Must be a number"}
                validNewOption={(inputValue: string) =>
                  !!inputValue &&
                  !isNaN(+inputValue) &&
                  !doesOptionExist(inputValue)
                }
                value={selectedQuantity}
                disabled={loading || !inventoryQty}
                options={options}
              />

              <RemoveItem>
                <Button type="text" onClick={handleRemove}>
                  Remove
                </Button>
              </RemoveItem>

              <Error>
                <TimedWarning error={error} delay="4000" />
              </Error>
            </StyledItemQuantitySelect>
          )}

          {!!product.subItems?.length && (
            <KitContents items={product.subItems} />
          )}

          <FlagAndNote
            product={product}
            quantity={quantity}
            inventoryQty={inventoryQty || 0}
            blockedItem={blockedItem}
            updateCartItem={updateCartItem}
            subscriptionIntervalInDays={subscriptionIntervalInDays || null}
            isGuest={isGuest}
          />
        </DetailsWrapper>

        {subtotalPrice && (
          <Subtotal price={subtotalPrice} quantity={quantity} small={small} />
        )}
      </StyledCartItem>

      {!hideDivider && <Divider />}
    </>
  );
};
